/**
 * Traduce lo marcado en el calendario a lo que pide `registerPayment`.
 *
 * Vive aparte porque lo usan dos pantallas: el cobro de un alumno y el cobro a
 * varios hermanos. Duplicarlo sería duplicar decisiones sobre dinero —a qué
 * cargo va cada quetzal, qué mora se está cobrando, qué mes se imprime en el
 * recibo— y basta con que una copia se quede atrás para que el papel diga una
 * cosa y el saldo otra.
 */

import {
  money,
  repartirDescuentoAnual,
  type AllocationInput,
  type Charge,
  type MonthlyChargeState,
} from "@senm/domain";
import type { SeleccionCalendario } from "../pages/Calendar.js";
import type { LocalCharge } from "../local/db.js";
import { toDomainCharges } from "./payments.js";

/** Todo lo marcado, meses y conceptos sueltos, en una sola lista. */
export function marcado(
  seleccion: SeleccionCalendario,
): { chargeId: string; totalMinor: number }[] {
  return [
    ...seleccion.elegidos.map((mes) => ({
      chargeId: mes.chargeId,
      totalMinor: mes.totalMinor,
    })),
    ...seleccion.otrosElegidos.map((cargo) => ({
      chargeId: cargo.chargeId,
      totalMinor: cargo.totalMinor,
    })),
  ];
}

/**
 * Reparto EXACTO de lo marcado: cada cargo recibe su importe, ni más ni menos.
 *
 * No se reparte por antigüedad. Repartir aquí podría aplicar el dinero a un mes
 * distinto del que se le dijo al encargado que estaba pagando, y el recibo
 * saldría con otro concepto.
 */
export function distribucionExacta(
  seleccion: SeleccionCalendario,
  descuento?: DescuentoAnual | null,
): AllocationInput[] {
  return marcado(seleccion).map((linea) => ({
    chargeId: linea.chargeId,
    amount: money(
      linea.totalMinor - (descuento?.porCargo.get(linea.chargeId) ?? 0),
      seleccion.currency,
    ),
  }));
}

/** Lo que se rebaja por pagar el año completo, repartido mes a mes. */
export interface DescuentoAnual {
  porcentaje: number;
  totalMinor: number;
  porCargo: Map<string, number>;
}

/**
 * El descuento por pago anual, si esta seleccion da derecho a él.
 *
 * Las condiciones son las MISMAS que valida el servidor, y por eso se
 * comprueban aquí antes de ofrecerlo: que estén marcados todos los meses del
 * ciclo, que ninguno tenga ya un abono y que ninguno arrastre mora. Ofrecerlo
 * cuando el servidor va a rechazarlo dejaría al cajero con el encargado
 * enfrente y un error que no puede resolver.
 *
 * Devuelve `null` cuando no corresponde, que es lo que apaga la casilla.
 */
export function descuentoAnualDisponible(
  seleccion: SeleccionCalendario,
  porcentaje: number,
): DescuentoAnual | null {
  if (seleccion.elegidos.length === 0) return null;
  // Un mes sin marcar, o uno ya pagado, significa que esto no es el pago del
  // año completo aunque lo parezca.
  if (seleccion.mesesPendientes.length > 0) return null;
  if (seleccion.mesesPagados.length > 0) return null;
  const limpios = seleccion.elegidos.every(
    (mes) =>
      mes.lateFeeMinor === 0 &&
      mes.capitalMinor === mes.estadoMora.originalAmount.minor,
  );
  if (!limpios) return null;

  const reparto = repartirDescuentoAnual(
    seleccion.elegidos.map((mes) => mes.estadoMora.originalAmount.minor),
    porcentaje,
  );
  return {
    porcentaje,
    totalMinor: reparto.reduce((suma, parte) => suma + parte, 0),
    porCargo: new Map(
      seleccion.elegidos.map((mes, indice) => [
        mes.chargeId,
        reparto[indice] ?? 0,
      ]),
    ),
  };
}

/**
 * Cargos del dispositivo, con la mora que trajo el calendario del servidor.
 *
 * El dispositivo no guarda mora —depende de la fecha operativa y por eso se
 * calcula en el servidor—, así que sin esto la validación local ve solo el
 * capital y rechaza un cobro legítimo: un mes con Q996 de mora fallaba con
 * «supera su saldo pendiente (Q400.00)», ignorando lo que la pantalla de arriba
 * acababa de mostrar.
 */
export function cargosConMora(
  charges: readonly LocalCharge[],
  seleccion: SeleccionCalendario,
): Charge[] {
  const porCargo = new Map(
    seleccion.elegidos.map((mes) => [mes.chargeId, mes.estadoMora]),
  );
  return toDomainCharges(charges).map((cargo) => {
    const estado = porCargo.get(cargo.id);
    return estado === undefined ? cargo : { ...cargo, monthlyState: estado };
  });
}

/** Estado de mora por cargo, para que cliente y servidor validen igual. */
export function estadosDeMora(
  seleccion: SeleccionCalendario,
): Map<string, MonthlyChargeState> {
  return new Map(
    seleccion.elegidos.map((mes) => [mes.chargeId, mes.estadoMora]),
  );
}

/**
 * Etiqueta del mes por cargo, para el recibo.
 *
 * La fila local de un cargo solo sabe su concepto y su importe; sin esto el
 * recibo salía con «Periodo» en blanco, que es de los primeros datos que el
 * encargado busca.
 */
export function etiquetasDeMes(
  seleccion: SeleccionCalendario,
): Map<string, string> {
  const etiquetas = new Map<string, string>();
  for (const mes of seleccion.elegidos) {
    if (mes.monthLabel !== null && mes.monthLabel !== undefined) {
      etiquetas.set(mes.chargeId, mes.monthLabel);
    }
  }
  return etiquetas;
}
